import React from 'react';
import { RegionCode, getRegion } from '../utils/region';
// Brand mark used site-wide (navbar, mobile menu). Each domain ships ONE
// artwork, exported WHITE — it renders as-is on dark surfaces and is inverted
// to black on light ones. The mark is static: it does not crop, swap, or
// animate on scroll.
const LOGO_BY_REGION: Record<RegionCode, {
  light: string;
  dark: string;
}> = {
  '253': {
    light: 'https://cdn.magicpatterns.com/uploads/npQqZiQ1q8dULzSaV3t1Rn/253code.png',
    dark: 'https://cdn.magicpatterns.com/uploads/npQqZiQ1q8dULzSaV3t1Rn/253code.png'
  },
  '206': {
    // Single white vector artwork. SVG stays crisp at every size, unlike the
    // raster PNGs it replaced.
    light: 'https://cdn.magicpatterns.com/uploads/huGT18BMo8yCSFXWekvhVv/206_svg.svg',
    dark: 'https://cdn.magicpatterns.com/uploads/huGT18BMo8yCSFXWekvhVv/206_svg.svg'
  },
  '425': {
    light: 'https://cdn.magicpatterns.com/uploads/kmHPuLicBRxog1QSdiQDmA/425code.png',
    dark: 'https://cdn.magicpatterns.com/uploads/kmHPuLicBRxog1QSdiQDmA/425code.png'
  }
};
/**
 * Resolve the active domain's logo image URL.
 */
export function getLogoSrc(region?: RegionCode, surface: 'light' | 'dark' = 'dark'): string {
  return LOGO_BY_REGION[region ?? getRegion()][surface];
}
// TEMPORARY GLOBAL HIDE — every logo across the site is suppressed
// while this flag is true (the Logo component below, plus the inline
// <img> logos in Navbar.tsx that import this constant). To restore the
// logos, flip this back to `false` and re-publish.
export const LOGOS_HIDDEN = false;
// On light surfaces the inverted-to-black artwork is held at 80% opacity so the
// solid black reads a touch softer against the navbar. On dark surfaces the
// white artwork renders at full strength.
const LOGO_OPACITY_CLASS = 'opacity-80 dark:opacity-100';
interface LogoProps {
  className?: string;
  imgClassName?: string;
  /**
   * The artwork is exported white. Leave this true to invert it to black on
   * light surfaces; pass false for call sites that are ALWAYS dark, so the
   * white artwork is used in both themes.
   */
  invertOnLight?: boolean;
  /**
   * Retained for backwards-compatibility with existing call sites. The logo is
   * now a single static brand mark, so these no longer affect rendering.
   */
  cycleEvery?: number;
  region?: RegionCode;
  pinned?: boolean;
  tinted?: boolean;
  tintColor?: string;
}
/**
 * 206Gravel brand mark — one white artwork, inverted to black on light
 * surfaces.
 */
export function Logo({
  className = '',
  imgClassName = 'h-10 w-auto',
  invertOnLight = true
}: LogoProps) {
  // While LOGOS_HIDDEN is true, every Logo instance renders nothing.
  // Flip the constant above to `false` (or remove this block) to restore.
  if (LOGOS_HIDDEN) return null;
  const src = getLogoSrc(getRegion(), 'light');
  return <span className={`inline-flex items-center ${className}`} data-id="element-1161" data-mp="7kbouw73zuhl">
      <img src={src} alt="206Gravel" className={`${imgClassName} block pointer-events-none ${LOGO_OPACITY_CLASS} ${invertOnLight ? 'invert dark:invert-0' : ''}`} data-id="element-1162" data-mp="bl5d5qogrbb0" />
    </span>;
}